import { useEffect, useState } from 'react'
import { sessionAuthService } from '@/services/session-auth-service'
import intercomService from '@/services/intercom-service'

declare global {
  interface Window {
    Intercom: any
    intercomSettings?: any
  }
}

interface IntercomProviderProps {
  children: React.ReactNode
}

export function IntercomProvider({ children }: IntercomProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => sessionAuthService.isAuthenticated())
  const [adminInfo, setAdminInfo] = useState(() => sessionAuthService.getAdminInfo())

  // Listen for session changes
  useEffect(() => {
    const handleSessionChange = () => {
      setIsAuthenticated(sessionAuthService.isAuthenticated())
      setAdminInfo(sessionAuthService.getAdminInfo())
    }

    sessionAuthService.addEventListener('sessionChanged', handleSessionChange)
    return () => {
      sessionAuthService.removeEventListener('sessionChanged', handleSessionChange)
    }
  }, [])

  // Load Intercom script
  useEffect(() => {
    const appId = import.meta.env.VITE_INTERCOM_APP_ID

    if (!window.Intercom && appId) {
      ;(function () {
        const w = window as any
        const ic = w.Intercom
        if (typeof ic === 'function') {
          ic('reattach_activator')
          ic('update', w.intercomSettings)
        } else {
          const d = document
          const i: any = function () {
            i.c(arguments)
          }
          i.q = []
          i.c = function (args: any) {
            i.q.push(args)
          }
          w.Intercom = i
          const l = function () {
            const s = d.createElement('script')
            s.type = 'text/javascript'
            s.async = true
            s.src = `https://widget.intercom.io/widget/${appId}`
            const x = d.getElementsByTagName('script')[0]
            x.parentNode?.insertBefore(s, x)
          }
          if (document.readyState === 'complete') {
            l()
          } else {
            w.addEventListener('load', l, false)
          }
        }
      })()
    }
  }, [])

  // Initialize/shutdown based on auth state
  useEffect(() => {
    if (isAuthenticated && adminInfo) {
      intercomService.initialize()
    } else if (!isAuthenticated) {
      intercomService.shutdown()
    }

    return () => {
      if (isAuthenticated) {
        intercomService.shutdown()
      }
    }
  }, [isAuthenticated, adminInfo])

  // Track route changes
  useEffect(() => {
    if (isAuthenticated && adminInfo) {
      intercomService.update()
    }
  }, [isAuthenticated, adminInfo])

  return <>{children}</>
}
